import React, { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { format } from 'date-fns'
import { id } from 'date-fns/locale'
import { useKegiatan, useTahapList, usePanitiaList, usePesertaList, useGaleriList, useLaporanList } from '@/hooks/useKegiatan'
import { useRapatList } from '@/hooks/useRapat'
import { useAuthStore } from '@/stores/authStore'
import { useAuth } from '@/hooks/useAuth'
import TabBar from '@/components/ui/TabBar'
import Badge from '@/components/ui/Badge'
import Card from '@/components/ui/Card'
import Button from '@/components/ui/Button'
import EmptyState from '@/components/ui/EmptyState'
import TahapProgress from '@/components/kegiatan/TahapProgress'
import PhotoGrid from '@/components/ui/PhotoGrid'
import pb from '@/lib/pocketbase'

const statusVariant: Record<string, 'primary' | 'green' | 'yellow' | 'red'> = {
  aktif: 'green',
  selesai: 'primary',
  draft: 'yellow',
  batal: 'red',
}

const statusLabel: Record<string, string> = {
  aktif: 'Aktif',
  selesai: 'Selesai',
  draft: 'Draft',
  batal: 'Batal',
}

const tabs = [
  { key: 'info', label: 'Info' },
  { key: 'stadion', label: 'Stadion' },
  { key: 'rapat', label: 'Rapat' },
  { key: 'peserta', label: 'Peserta' },
  { key: 'galeri', label: 'Galeri' },
  { key: 'laporan', label: 'Laporan' },
]

export default function KegiatanDetailPage() {
  const { id: kegiatanId } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { isSuperAdmin, isStadion } = useAuth()
  const [tab, setTab] = useState('info')

  const { data: kegiatan, isLoading: loadingKegiatan } = useKegiatan(kegiatanId)
  const { data: tahap = [] } = useTahapList(kegiatanId)
  const { data: panitia = [] } = usePanitiaList(kegiatanId)
  const { data: peserta = [] } = usePesertaList(kegiatanId)
  const { data: galeri = [] } = useGaleriList(kegiatanId)
  const { data: laporanResult } = useLaporanList({ kegiatan: kegiatanId, published: true })
  const { data: rapat = [] } = useRapatList(kegiatanId)

  const laporanItems = laporanResult?.items || []

  if (loadingKegiatan) {
    return (
      <div className="py-4 space-y-3">
        <div className="bg-white rounded-card h-48 animate-pulse" />
        <div className="bg-white rounded-card h-32 animate-pulse" />
      </div>
    )
  }

  if (!kegiatan) {
    return (
      <EmptyState
        title="Kegiatan tidak ditemukan"
        description="Kegiatan yang Anda cari tidak ada."
        action={{ label: 'Kembali', onClick: () => navigate('/dashboard') }}
      />
    )
  }

  const fotoItems = galeri.flatMap((g) =>
    (Array.isArray(g.foto) ? g.foto : [g.foto]).map((f) => ({
      id: `${g.id}-${f}`,
      url: pb.files.getUrl(g, f as string, { thumb: '400x400' }),
    }))
  )

  return (
    <div className="py-4 space-y-3">
      <Card>
        <div className="flex gap-2 mb-3">
          <Badge variant={statusVariant[kegiatan.status]}>{statusLabel[kegiatan.status]}</Badge>
          <Badge variant="blue">{kegiatan.jenis}</Badge>
        </div>
        <h1 className="text-lg font-bold text-text-primary mb-1">{kegiatan.judul}</h1>
        {kegiatan.tema && <p className="text-sm text-text-secondary italic mb-3">{kegiatan.tema}</p>}

        <div className="space-y-2">
          <div className="flex items-start gap-2 text-sm">
            <svg className="w-4 h-4 mt-0.5 text-text-secondary flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span className="text-text-secondary">
              {format(new Date(kegiatan.tanggal_mulai), 'EEEE, dd MMMM yyyy', { locale: id })}
              {kegiatan.tanggal_selesai !== kegiatan.tanggal_mulai && (
                <> — {format(new Date(kegiatan.tanggal_selesai), 'dd MMMM yyyy', { locale: id })}</>
              )}
            </span>
          </div>
          <div className="flex items-start gap-2 text-sm">
            <svg className="w-4 h-4 mt-0.5 text-text-secondary flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span className="text-text-secondary">{kegiatan.lokasi}</span>
          </div>
          <div className="flex items-start gap-2 text-sm">
            <svg className="w-4 h-4 mt-0.5 text-text-secondary flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span className="text-text-secondary">Target: {kegiatan.target_peserta} peserta</span>
          </div>
        </div>

        {isSuperAdmin && (
          <div className="flex gap-2 mt-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/kegiatan/${kegiatanId}/edit`)}
            >
              Edit
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/kegiatan/${kegiatanId}/presensi`)}
            >
              Presensi
            </Button>
          </div>
        )}
      </Card>

      <TabBar tabs={tabs} activeTab={tab} onChange={setTab} />

      {tab === 'info' && (
        <div className="space-y-3">
          {kegiatan.deskripsi && (
            <Card>
              <h3 className="text-sm font-semibold mb-2">Deskripsi</h3>
              <p className="text-sm text-text-secondary whitespace-pre-line">{kegiatan.deskripsi}</p>
            </Card>
          )}
          {kegiatan.latar_belakang && (
            <Card>
              <h3 className="text-sm font-semibold mb-2">Latar Belakang</h3>
              <p className="text-sm text-text-secondary whitespace-pre-line">{kegiatan.latar_belakang}</p>
            </Card>
          )}
          {kegiatan.maksud_tujuan && (
            <Card>
              <h3 className="text-sm font-semibold mb-2">Maksud & Tujuan</h3>
              <p className="text-sm text-text-secondary whitespace-pre-line">{kegiatan.maksud_tujuan}</p>
            </Card>
          )}
          <Card>
            <h3 className="text-sm font-semibold mb-3">Progress Tahap</h3>
            <TahapProgress tahap={tahap} />
          </Card>
        </div>
      )}

      {tab === 'stadion' && (
        <div className="space-y-3">
          {panitia.length === 0 ? (
            <EmptyState
              title="Belum ada panitia"
              description="Panitia belum ditambahkan."
            />
          ) : (
            panitia.map((p) => (
              <Card key={p.id}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="text-sm font-bold text-primary">
                      {(p.nama || '?').slice(0, 2).toUpperCase()}
                    </span>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold">{p.nama}</p>
                    <p className="text-xs text-text-secondary">{p.role_panitia}</p>
                    {p.telepon && <p className="text-xs text-text-secondary">{p.telepon}</p>}
                  </div>
                </div>
              </Card>
            ))
          )}
          {isSuperAdmin && (
            <Button
              block
              onClick={() => navigate(`/kegiatan/${kegiatanId}/panitia/new`)}
            >
              Tambah Stadion
            </Button>
          )}
        </div>
      )}

      {tab === 'rapat' && (
        <div className="space-y-3">
          {rapat.length === 0 ? (
            <EmptyState
              title="Belum ada rapat"
              description="Rapat untuk kegiatan akan muncul di sini."
            />
          ) : (
            rapat.map((r) => {
              const statusColor: Record<string, string> = {
                selesai: 'bg-green-100 text-green-700',
                berlangsung: 'bg-blue-100 text-blue-700',
                rencana: 'bg-yellow-100 text-yellow-700',
                dibatalkan: 'bg-red-100 text-red-700',
              }
              const statusLabel: Record<string, string> = {
                selesai: 'Selesai',
                berlangsung: 'Berlangsung',
                rencana: 'Rencana',
                dibatalkan: 'Batal',
              }
              return (
                <Card
                  key={r.id}
                  className="cursor-pointer hover:shadow-card transition-shadow"
                  onClick={() => navigate(`/kegiatan/${kegiatanId}/rapat/${r.id}`)}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-lg flex flex-col items-center justify-center flex-shrink-0 text-white text-xs font-bold ${r.status === 'selesai' ? 'bg-green-500' : r.status === 'berlangsung' ? 'bg-blue-500' : r.status === 'rencana' ? 'bg-yellow-500' : 'bg-red-500'}`}>
                      <span>{format(new Date(r.tanggal), 'dd')}</span>
                      <span>{format(new Date(r.tanggal), 'MMM')}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-sm font-semibold truncate">{r.judul}</p>
                        <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${statusColor[r.status] || ''}`}>
                          {statusLabel[r.status] || r.status}
                        </span>
                      </div>
                      <p className="text-xs text-text-secondary">
                        {r.waktu_mulai && r.waktu_selesai
                          ? `${r.waktu_mulai} - ${r.waktu_selesai} WIB`
                          : 'Waktu belum ditentukan'}
                        {r.tempat && ` · ${r.tempat}`}
                      </p>
                      {r.notulis_id && r.expand?.notulis_id && (
                        <p className="text-xs text-text-secondary mt-0.5">
                          Notulis: {r.expand.notulis_id.name || r.expand.notulis_id.username}
                        </p>
                      )}
                      {r.hasil_rapat && r.status === 'selesai' && (
                        <p className="text-xs text-text-secondary mt-1 italic truncate">
                          Hasil: {r.hasil_rapat.replace(/<[^>]+>/g, '').slice(0, 80)}...
                        </p>
                      )}
                    </div>
                  </div>
                </Card>
              )
            })
          )}
          {(isSuperAdmin || isStadion) && (
            <Button
              block
              onClick={() => navigate(`/kegiatan/${kegiatanId}/rapat/new`)}
            >
              + Tambah Rapat
            </Button>
          )}
        </div>
      )}

      {tab === 'peserta' && (
        <div className="space-y-3">
          <Card>
            <div className="flex justify-between items-center">
              <span className="text-sm font-semibold">Total Peserta</span>
              <span className="text-lg font-bold text-primary">{peserta.length}</span>
            </div>
          </Card>
          {peserta.length === 0 ? (
            <EmptyState title="Belum ada peserta" />
          ) : (
            peserta.slice(0, 5).map((p) => (
              <Card key={p.id}>
                <p className="text-sm font-semibold">{p.nama}</p>
                {p.nbm && <p className="text-xs text-text-secondary">NBM: {p.nbm}</p>}
                <Badge variant={p.status_pembayaran === 'lunas' ? 'green' : 'red'} className="mt-1">
                  {p.status_pembayaran === 'lunas' ? 'Lunas' : 'Belum Bayar'}
                </Badge>
              </Card>
            ))
          )}
          {isSuperAdmin && (
            <Button
              block
              onClick={() => navigate(`/kegiatan/${kegiatanId}/peserta/new`)}
            >
              Tambah Peserta
            </Button>
          )}
        </div>
      )}

      {tab === 'galeri' && (
        <div>
          {fotoItems.length === 0 ? (
            <EmptyState
              title="Belum ada foto"
              description="Upload foto kegiatan di sini."
            />
          ) : (
            <PhotoGrid photos={fotoItems} maxDisplay={18} />
          )}
          {isSuperAdmin && (
            <div className="mt-3">
              <Button block variant="outline">
                Upload Foto
              </Button>
            </div>
          )}
        </div>
      )}

      {tab === 'laporan' && (
        <div className="space-y-3">
          {laporanItems.length === 0 ? (
            <EmptyState
              title="Belum ada laporan"
              description="Laporan kegiatan akan muncul di sini."
            />
          ) : (
            laporanItems.map((l) => (
              <Card
                key={l.id}
                onClick={() => navigate(`/laporan/viewer/${l.id}`)}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <Badge variant="primary">{l.jenis}</Badge>
                    <h3 className="text-sm font-semibold mt-1">{l.judul}</h3>
                    <p className="text-xs text-text-secondary mt-0.5">
                      {format(new Date(l.created), 'dd MMM yyyy', { locale: id })}
                    </p>
                  </div>
                  {l.published && (
                    <Badge variant="green">Published</Badge>
                  )}
                </div>
              </Card>
            ))
          )}
          {(isSuperAdmin || true) && (
            <Button
              block
              onClick={() => navigate(`/laporan/new?kegiatan=${kegiatanId}`)}
            >
              Tambah Laporan
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
